import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { animated, useSpring } from 'react-spring';
import styled from '@emotion/styled';
import { Box, Flex, Image, Separator, useToken } from '@chakra-ui/react';
import { X } from 'lucide-react';
import {
  Heading,
  HStack,
  Pressable,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { useClickOrDrag } from '../hooks/useClickOrDrag';
import type { EmotionTheme } from '../theme/emotionTheme';

const RESIZE_HANDLE_CLASS = 'drawer-resize-handle';
const DRAWER_MIN_WIDTH_PX = 496;
const LOCAL_STORAGE_DRAWER_WIDTH_KEY = 'hc-drawer-width-v1.1';

const iconSvgUri = '/corner-down-right.svg';

const DrawerWrapper = styled(animated.div)`
  max-width: 100%;
`;

type ResizeHandleProps = { isResizing: boolean; theme?: EmotionTheme };

const ResizeHandle = styled('div')<ResizeHandleProps>`
  background-color: ${(p) =>
    p.isResizing
      ? p.theme!.colors.primary['600']
      : p.theme!.colors.muted['400']};
  border: none;
  cursor: col-resize;
  height: 100%;
  position: absolute;
  width: 2px;
  z-index: 99;
`;

export type DrawerHeaderAction = {
  title: string;
  onPress: () => void;
  primary: boolean;
  disabled?: boolean;
};

export type DrawerProps = {
  bgColor?: string;
  children?: ReactNode;
  headerSubtitle?: string;
  onClose?: () => void;
  header?: string;
  isOpen?: boolean;
  showBackButton?: boolean;
  showExternalCloseHandle?: boolean;
  showHeader?: boolean;
  headerActions?: DrawerHeaderAction[];
  renderContent?: (isResizing: boolean) => ReactNode;
};

export const Drawer = ({
  bgColor = '',
  children = null,
  headerSubtitle = '',
  onClose = () => {},
  header = '',
  isOpen = false,
  showBackButton = true,
  showExternalCloseHandle = true,
  showHeader = true,
  headerActions = [],
  renderContent,
}: DrawerProps) => {
  const [muted100, muted400, secondary200] = useToken('colors', [
    'muted.100',
    'muted.400',
    'secondary.200',
  ]);
  const { onMouseDown, onMouseMove, onMouseUp } = useClickOrDrag();

  const screenWidth = window.innerWidth;
  const drawerMaxWidth = screenWidth * 0.7;
  const backBttn = './../chevron-left.png';
  const drawerRef = useRef<HTMLDivElement>(null);

  const [isVisible, setIsVisible] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [drawerWidthDelta, setDrawerWidthDelta] = useState(0);
  const drawerBackgroundColor = bgColor || muted100;
  const headerHeightPx = headerSubtitle ? 106 : 70;

  const initialWidth = localStorage.getItem(LOCAL_STORAGE_DRAWER_WIDTH_KEY)
    ? `${localStorage.getItem(LOCAL_STORAGE_DRAWER_WIDTH_KEY)}px`
    : '30%';

  const style = useSpring({
    backgroundColor: drawerBackgroundColor,
    borderLeft: `1px solid ${muted400}`,
    height: '100vh',
    maxWidth: `${drawerMaxWidth}px`,
    minWidth: `${DRAWER_MIN_WIDTH_PX}px`,
    position: 'fixed' as const,
    right: isVisible ? 0 : -screenWidth,
    top: 0,
    width: isVisible ? undefined : initialWidth,
  });

  const readCurrentWidth = () =>
    parseInt(
      document.defaultView!.getComputedStyle(drawerRef.current!).width,
      10
    );

  const stopResizing = useCallback(() => {
    setIsResizing(false);
    const currentWidth = Math.max(DRAWER_MIN_WIDTH_PX, readCurrentWidth());
    localStorage.setItem(LOCAL_STORAGE_DRAWER_WIDTH_KEY, String(currentWidth));
  }, []);

  const resize = useCallback(
    (mouseMoveEvent: MouseEvent) => {
      if (!isResizing) return;

      setDrawerWidthDelta(
        mouseMoveEvent.clientX -
          drawerRef.current!.getBoundingClientRect().left
      );
    },
    [isResizing]
  );

  useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [resize, stopResizing]);

  useEffect(() => {
    if (!drawerRef.current) return;
    const currentWidth = readCurrentWidth();
    drawerRef.current.style.width = +currentWidth - drawerWidthDelta + 'px';
  }, [drawerWidthDelta]);

  // Automatically react and set visible state
  // if isOpen has been imperatively set from the outside
  useLayoutEffect(() => {
    setIsVisible(isOpen);
  }, [isOpen]);

  return (
    <DrawerWrapper
      style={style}
      ref={drawerRef}
      onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
        if ((e.target as HTMLElement).classList.contains(RESIZE_HANDLE_CLASS))
          e.preventDefault();
      }}
    >
      <ResizeHandle
        className={RESIZE_HANDLE_CLASS}
        isResizing={isResizing}
        onMouseDown={() => setIsResizing(true)}
      >
        {showExternalCloseHandle && (
          <Box
            display={'flex'}
            alignItems={'center'}
            bgColor={'#F7F5ED'}
            borderLeftRadius={'8px'}
            cursor={isResizing ? 'col-resize' : 'pointer'}
            h={'40px'}
            justifyContent={'center'}
            left={'-41px'}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={(e) => onMouseUp(e, onClose)}
            onClick={onClose}
            position={'absolute'}
            shadow={'lg'}
            w={'40px'}
          >
            <Image boxSize={'16px'} src={iconSvgUri} />
          </Box>
        )}
      </ResizeHandle>

      <VStack>
        {showHeader && (
          <VStack
            backgroundColor={drawerBackgroundColor}
            position="sticky"
            top={0}
            zIndex={11}
          >
            <Flex
              alignItems="flex-start"
              flexDirection="row"
              justifyContent="space-between"
              p={6}
            >
              <VStack flex={1} pr={2}>
                <HStack alignItems="center">
                  {showBackButton && (
                    <Pressable onClick={onClose} mr={2}>
                      <Image src={backBttn} boxSize={'32px'} />
                    </Pressable>
                  )}
                  <Heading size="md">{header}</Heading>
                  <HStack ml={'auto'} alignSelf={'flex-start'}>
                    {headerActions?.map(
                      ({ disabled, onPress, primary, title }) => (
                        <Pressable
                          disabled={disabled}
                          key={title}
                          py={1}
                          px={2}
                          onClick={onPress}
                          borderWidth={1}
                          mx={1}
                          bgColor={primary ? 'primary.50' : 'white'}
                          borderRadius={'full'}
                          borderColor={'cloud.200'}
                        >
                          <Text
                            fontWeight={600}
                            color={'secondary.200'}
                            fontSize={'xs'}
                          >
                            {title}
                          </Text>
                        </Pressable>
                      )
                    )}
                  </HStack>
                </HStack>

                {!!headerSubtitle && (
                  <Text color={'blueGray.600'} fontSize={'sm'} mt={1}>
                    {headerSubtitle}
                  </Text>
                )}
              </VStack>

              <Pressable onClick={onClose}>
                <X color={secondary200} size={20} strokeWidth={2.5} />
              </Pressable>
            </Flex>

            <Separator />
          </VStack>
        )}

        <VStack
          h={showHeader ? `calc(100vh - ${headerHeightPx}px)` : '100vh'}
          overflow="auto"
          p={showHeader ? 6 : undefined}
        >
          {!!renderContent ? renderContent(isResizing) : children}
        </VStack>
      </VStack>
    </DrawerWrapper>
  );
};
